<script setup lang="ts">
    import { computed, ref } from 'vue';
    import ModalBase from '@/shared/components/ModalBase.vue';

    type SaveMode = 'new' | 'overwrite';

    const props = defineProps({
        packageName: {
            type: String,
            required: true
        }
    });

    const emit = defineEmits<{
        saveAsNew: [name: string];
        overwrite: [];
        cancel: [];
    }>();

    const mode = ref<SaveMode>('new');
    const newName = ref<string>(props.packageName);

    const canSave = computed(() => mode.value === 'overwrite' || newName.value.trim().length > 0);

    function onSave(): void {
        if (!canSave.value)
            return;

        if (mode.value === 'overwrite') {
            emit('overwrite');
        } else {
            emit('saveAsNew', newName.value.trim());
        }
    }
</script>

<template>
    <modal-base
        @on-cancel="emit('cancel')">
        <template #modal-header>
            Save changes
        </template>
        <template #modal-body>
            <p class="block">
                A package holds a single export file, so the changed setup must be exported again.
            </p>
            <b-field>
                <b-radio
                    v-model="mode"
                    native-value="new">
                    <b>Create a new package</b> with this setup. The original package is kept unchanged.
                </b-radio>
            </b-field>
            <b-field
                v-if="mode === 'new'"
                label="Name of new package"
                class="ml-5">
                <b-input
                    v-model="newName"
                    maxlength="256" />
            </b-field>
            <b-field>
                <b-radio
                    v-model="mode"
                    native-value="overwrite">
                    <b>Overwrite this package.</b> The package is exported again and the existing export file is replaced.
                </b-radio>
            </b-field>
            <b-message
                v-if="mode === 'overwrite'"
                type="is-warning"
                class="ml-5">
                The package keeps its id. If the previous export file has already been uploaded to another
                environment, delete the package there before uploading the new file.
            </b-message>
        </template>
        <template #modal-footer>
            <div class="buttons">
                <b-button
                    label="Cancel"
                    @click="emit('cancel')" />
                <b-button
                    :label="mode === 'overwrite' ? 'Overwrite package' : 'Create new package'"
                    :type="mode === 'overwrite' ? 'is-warning' : 'is-primary'"
                    :disabled="!canSave"
                    @click="onSave" />
            </div>
        </template>
    </modal-base>
</template>
